import { takeLatest, put, call } from "redux-saga/effects";
import axios from "axios";
import { toast } from "react-toastify";
import { select } from "redux-saga/effects"

import {
  getAppConfig,
  getAppConfigSuccess,
  getAppConfigFailure,
  getBenefitsAndFeatures,
  getBenefitsAndFeaturesSuccess,
  getBenefitsAndFeaturesFailure,
  updateConfigRequest,
  updateConfigSuccess,
  updateConfigFailure,
  getConfiguration,
  getConfigurationSuccess,
  getConfigurationFailure,
  updateSingleConfigRequest,
  updateSingleConfigSuccess,
  updateSingleConfigFailure,
  saveAddressRequest,
  saveAddressSuccess,
  saveAddressFailure,
  saveFirebaseSettingRequest,
  saveFirebaseSettingSuccess,
  saveFirebaseSettingFailure,
  saveAdminFirebaseSettingRequest,
  saveAdminFirebaseSettingSuccess,
  saveAdminFirebaseSettingFailure,
  getInvoiceTitleRequest,
  getInvoiceTitleSuccess,
  getInvoiceTitleFailure,
} from "./configurationSlice";


function* fetchAppConfigSaga(action) {
  try {
    const response = yield call(() =>
      axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/v1/setting/getAppConfig`)
    );

    yield put(getAppConfigSuccess(response.data.data));

    const configData = yield select((state) => state.appConfig.configData);

    if (configData && Object.keys(configData).length > 0) {
      if (action.payload?.onSuccess && typeof action.payload.onSuccess === "function") {
        console.log("Store updated, reloading page");
        action.payload.onSuccess();
      }
    }
  } catch (error) {
    yield put(getAppConfigFailure(error.message));
    toast.error(`Failed to fetch app config: ${error.message}`);
  }
}


// Other sagas (unchanged)
function* getBenefitsAndFeaturesSaga() {
  try {
    const response = yield call(() =>
      axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/v1/setting/getbenefitsandfeatures`)
    );
    yield put(getBenefitsAndFeaturesSuccess(response.data.data));
  } catch (error) {
    yield put(getBenefitsAndFeaturesFailure(error.message));
    toast.error(`Failed to fetch benefits and features: ${error.message}`);
  }
}

function* updateConfigSaga({ payload }) {
  try {
    yield call(axios.patch, `${process.env.REACT_APP_API_BASE_URL}/api/v1/setting/updateKeyValue`, payload);
    yield put(updateConfigSuccess(payload));
    yield put(getAppConfig());
    toast.success("Configuration updated successfully!");
  } catch (error) {
    const message = error.response?.data?.message || error.message;
    yield put(updateConfigFailure(message));
    toast.error(`Failed to update config: ${message}`);
  }
}

function* fetchConfigurationSaga() {
  try {
    const response = yield call(() =>
      axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/setting/getConfiguration`
      )
    );
    const configData = response.data?.data;
    yield put(getConfigurationSuccess(configData));
  } catch (error) {
    yield put(getConfigurationFailure(error.message));
    toast.error(`Failed to load configuration: ${error.message}`);
  }
}

function* updateSingleConfigSaga({ payload }) {
  try {
    const response = yield call(
      axios.post,
      `${process.env.REACT_APP_API_BASE_URL}/api/v1/setting/updateSingleConfig`,
      payload
    );
    const { data } = response.data;
    yield put(updateSingleConfigSuccess(data));
    yield put(getConfiguration());
    toast.success("Configuration updated successfully!");
  } catch (error) {
    const message = error.response?.data?.message || error.message;
    yield put(updateSingleConfigFailure(message));
    toast.error(`Failed to update setting: ${message}`);
  }
}

function* saveAddressSaga({ payload }) {
  try {
    const response = yield call(
      axios.post,
      `${process.env.REACT_APP_API_BASE_URL}/api/v1/setting/saveAddress`,
      payload
    );

    const message = response.data?.message;
    yield put(saveAddressSuccess(payload));
    yield put(getConfiguration());
    toast.success(message || "Address saved successfully");
  } catch (error) {
    const message = error.response?.data?.message || error.message;
    yield put(saveAddressFailure(message));
    toast.error(`Failed to save address: ${message}`);
  }
}

function* saveFirebaseSettingSaga({ payload }) {
  try {
    const { firebaseProjectId, topicName, file } = payload.value;

    const formData = new FormData();
    formData.append("firebaseProjectId", firebaseProjectId);
    formData.append("topicName", topicName);
    formData.append("file", file);

    const response = yield call(() =>
      axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/setting/saveFirebaseSetting`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      )
    );

    const message = response.data?.message || "Firebase setting saved";
    yield put(saveFirebaseSettingSuccess());
    yield put(getConfiguration());
    toast.success(message);
  } catch (error) {
    const message = error.response?.data?.message || error.message;
    yield put(saveFirebaseSettingFailure(message));
    toast.error(`Failed to save firebase setting: ${message}`);
  }
}

function* saveAdminFirebaseSettingSaga({ payload }) {
  try {
    const { firebaseProjectId, file } = payload.value;

    const formData = new FormData();
    formData.append("firebaseProjectId", firebaseProjectId);
    formData.append("file", file);

    const response = yield call(() =>
      axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/setting/saveAdminFirebaseSetting`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      )
    );

    const message =
      response.data?.message || "Admin Firebase setting saved";
    yield put(saveAdminFirebaseSettingSuccess());
    yield put(getConfiguration());
    toast.success(message);
  } catch (error) {
    const message = error.response?.data?.message || error.message;
    yield put(saveAdminFirebaseSettingFailure(message));
    toast.error(`Failed to save admin firebase setting: ${message}`);
  }
}

function* fetchInvoiceTitleSaga({ payload }) {
  try {
    const response = yield call(() =>
      axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/setting/get-invoice-title`,
        { jeweller: payload.jeweller },
      )
    );

    const data = response.data?.data;

    yield put(getInvoiceTitleSuccess(data));
  } catch (error) {
    const message = error.response?.data?.message || error.message;
    yield put(getInvoiceTitleFailure(message));

    toast.error(`Failed to fetch invoice title: ${message}`);
  }
}

export function* appConfigSaga() {
  yield takeLatest(getAppConfig.type, fetchAppConfigSaga);
  yield takeLatest(updateConfigRequest.type, updateConfigSaga);
  yield takeLatest(getConfiguration.type, fetchConfigurationSaga);
  yield takeLatest(updateSingleConfigRequest.type, updateSingleConfigSaga);
  yield takeLatest(saveAddressRequest.type, saveAddressSaga);
  yield takeLatest(saveFirebaseSettingRequest.type, saveFirebaseSettingSaga);
  yield takeLatest(
    saveAdminFirebaseSettingRequest.type,
    saveAdminFirebaseSettingSaga
  );
  yield takeLatest(getBenefitsAndFeatures.type, getBenefitsAndFeaturesSaga);
  yield takeLatest(getInvoiceTitleRequest.type, fetchInvoiceTitleSaga);
}
